import { FieldValues, RegisterOptions } from 'react-hook-form';
import { SEPA_COUNTRIES } from '@components/forms/company-settings-form/src/config/utils';
import { COMPANY_BANKING_DETAILS_COUNTRY } from '@domain/company-settings';
import { DATE_PATTERN, emailValidator, textValidator, validateMultiEmail } from '@utils/validations/text-validators';
import { TFunction } from 'i18next';
import { validateIBAN } from 'ibantools';
import parsePhoneNumberFromString from 'libphonenumber-js';

import { Field } from './dynamic-input-fields.type';
import { REQUIRED_TEXT_ERROR } from './utils';

const EMAIL_FIELD_FORMAT_TEXT_ERROR = 'errors:emailFormFieldInvalidFormat';
const TEXT_FIELD_FORMAT_TEXT_ERROR = 'errors:textFormFieldInvalidCharacters';
const PHONE_ERROR_MESSAGE = 'errors:phoneFormFieldInvalidCharacters';
const DATE_ERROR_MESSAGE = 'errors:dateFormFieldInvalidCharacters';
const DATE_FUTURE_ERROR_MESSAGE = 'errors:dateFormFieldFutureDate';

const parseDate = (dateString: string): Date => {
  const [day, month, year] = dateString.split('/').map(Number);
  const newDate = `${month}/${day}/${year}`;
  return new Date(newDate);
};

export const getBasicValidationRules = (
  field: Field,
  t: TFunction,
  optionalFields?: boolean
): Partial<RegisterOptions> => {
  if (optionalFields || !field.isRequired) {
    return {};
  }

  return { required: t(REQUIRED_TEXT_ERROR) };
};

export const getEmailValidation = (t: TFunction): Partial<RegisterOptions> => ({
  pattern: {
    value: emailValidator,
    message: t(EMAIL_FIELD_FORMAT_TEXT_ERROR),
  },
});

export const getMultiEmailValidation = (field: Field, t: TFunction): Partial<RegisterOptions> => ({
  validate: (value: string) =>
    validateMultiEmail(value, field.isRequired, t(REQUIRED_TEXT_ERROR), t(EMAIL_FIELD_FORMAT_TEXT_ERROR)),
});

export const getTextValidation = (t: TFunction): Partial<RegisterOptions> => ({
  pattern: {
    value: textValidator,
    message: t(TEXT_FIELD_FORMAT_TEXT_ERROR),
  },
});

export const getPhoneValidation = (t: TFunction): Partial<RegisterOptions> => ({
  validate: (value: string) =>
    value ? parsePhoneNumberFromString(value)?.isValid() ?? t(PHONE_ERROR_MESSAGE) : undefined,
});

export const getDateValidation = (t: TFunction): Partial<RegisterOptions> => ({
  validate: (value: string) => {
    if (!value) return undefined;
    const enteredDate = parseDate(value);
    const currentDate = new Date();
    const invalidDate = !(!!value && DATE_PATTERN.test(value));

    if (invalidDate) return t(DATE_ERROR_MESSAGE);
    return enteredDate < currentDate || t(DATE_FUTURE_ERROR_MESSAGE);
  },
});

export const getBankValidation = (type: string, t: TFunction): Partial<RegisterOptions> => {
  if (type === 'iban') {
    return {
      validate: {
        validIban: (value: string, formValues: FieldValues) => {
          const country = String(formValues?.[COMPANY_BANKING_DETAILS_COUNTRY]);
          const isValidCountry = SEPA_COUNTRIES.includes(country);
          return !value || !isValidCountry || validateIBAN(value).valid || t('errors:invalidIban');
        },
      },
    };
  }
  return {};
};
